import { naytaPvm } from "./pvm";
import { useLataus, LatausTila } from "./Lataus";
import { EiKohteita } from "./EiKohteita";
import { useKohdeSynkka } from "./Kohdekonteksti";
import { useEffect, useState, type CSSProperties } from "react";
import { useViimeisin } from "./tuoreus";
import { api, type Kayttaja, type Kohde, type Tyopaketti, type Linja, type Resurssi,
  type TyopakettiMateriaalit, type TyopakettiTarve } from "./api";
import Liitteet from "./Liitteet";
import { Ohje } from "./Ohje";
import { t as tr, type KaannosAvain } from "./kieli";

/* Työpaketit: nimetty nippu linjoja (media + DN) + tekijät + tila.
   Sarana joka kytkee hitsauslokin, materiaalin ja miehityksen yhteen.
   Etenemä johdetaan paketin linjojen hitseistä — ei omaa kirjausta. */

const TILAT = ["pyydetty", "esitäytetty", "suunniteltu", "käynnissä", "valmis"];
/** Tila on palvelimen arvo (lähetetään sellaisenaan); vain näyttöteksti käännetään. */
const TILAN_AVAIN: Record<string, KaannosAvain> = {
  "pyydetty": "tyo.tila.pyydetty", "esitäytetty": "tyo.tila.esitaytetty", "suunniteltu": "tyo.tila.suunniteltu",
  "käynnissä": "tyo.tila.kaynnissa", "valmis": "tyo.tila.valmis",
};
const tilanNimi = (tila: string) => (TILAN_AVAIN[tila] ? tr(TILAN_AVAIN[tila]!) : tila);

export function Tyopaketit({ kayttaja }: { kayttaja: Kayttaja }) {
  const saaMuokata = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [valittu, setValittu] = useState<number | "">("");
  // T373 (Toni 15.10.2026): 3-viikkoissuunnitelman linkki #/tyopaketit?paketti=<id> vierittää paketin näkyviin ja korostaa sen.
  const [korostettu] = useState<number | null>(() => { const m = /[?&]paketti=(\d+)/.exec(window.location.hash); return m ? Number(m[1]) : null; });
  // T426: yläpalkin kohde ohjaa. «Kaikki kohteet» ei kelpaa tälle näkymälle, joten silloin näkymä pitää oman oletuksensa
  // ja valitsimensa eikä työnnä arvaustaan yläpalkkiin.
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(valittu, setValittu, { kaikkiSallittu: false });
  const [paketit, setPaketit] = useState<Tyopaketti[]>([]);
  const [resurssit, setResurssit] = useState<Resurssi[]>([]);
  const [lataa, setLataa] = useState(false);
  const [arkistoidut, setArkistoidut] = useState(false);

  const klKohteet = useLataus();
  const [kohteetHaettu, setKohteetHaettu] = useState(false);   // T685: kohdelistan oma tila (yhteinen lataus voi kaatua henkilöstöön)
  useEffect(() => {
    const kohteetHaku = api.kohteet().then((k) => {
      setKohteet(k); setKohteetHaettu(true);
      setValittu((v) => (v === "" && k.length ? k[0]!.id : v));
    });
    /* Tilaajalle resurssit on kielletty (API `onAsiakastunnus` → 403 ei_tilaajalle), eikä kutsua
       tehdä: lista tarvitaan vain tekijän lisäykseen (26.9.2026, kävely: valikkorivi kieltäytyi). */
    klKohteet.seuraa(Promise.all([kohteetHaku,
      kayttaja.laji !== "ulkopuolinen" ? api.resurssit().then(setResurssit) : Promise.resolve()]));
  }, [kayttaja.laji, klKohteet.kerta]);

  const uusi = useViimeisin();   // päätös 99: kortti saa kohteen valinnasta, paketit vastauksesta — niiden on oltava samaa kohdetta
  const paivita = () => {
    if (valittu === "") { setPaketit([]); return; }
    const tuore = uusi();
    setLataa(true);
    api.tyopaketit(valittu, arkistoidut).then((p) => { if (tuore()) setPaketit(p); }).catch(() => { if (tuore()) setPaketit([]); })
      .finally(() => { if (tuore()) setLataa(false); });
  };
  useEffect(paivita, [valittu, arkistoidut]);
  useEffect(() => {
    if (korostettu != null && paketit.some((p) => p.id === korostettu))
      document.querySelector(`[data-tyopaketti="${korostettu}"]`)?.scrollIntoView({ block: "center" });
  }, [paketit, korostettu]);

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{tr("valikko.tyopaketit")}</h1>
          {/* 238 (Toni 13.10.2026 «ei poistoja»): arkistoidut näkyvät pyydettäessä, palautettavissa. */}
          <label className="dim"><input type="checkbox" checked={arkistoidut} data-tyopaketit-arkistoidut=""
            onChange={(e) => setArkistoidut(e.target.checked)} /> {tr("tyo.nayta_arkistoidut")}</label></div>
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet_henkilot" />
      <EiKohteita haettu={kohteetHaettu} kohteita={kohteet.length} />

      {/* Ohje ylös (Toni 24.9.2026). «Kuka» = vaadiRooli-portit /api/tyopaketit*; muokkausnapit näkyvät samalla
          ehdolla (saaMuokata), ennen tätä lukija näki napit ja sai 403:n. */}
      <Ohje
        kuka={<>{tr("tyo.ohje.kuka")}</>}
        luvut={<>{tr("tyo.ohje.luvut")}</>} />

      {/* data-sisalto: kävelyn taitemittaus (25.9.2026) — ilman valittua kohdetta ensimmäinen sisältö on valinta. */}
      <div style={{ margin: "0 0 16px" }} data-sisalto="">
        {/* T539: «Kohde: … (yläpalkissa)» pois — kohde näkyy yläpalkissa (sama kuin Hub). */}
        {!(ylapalkissa && ylaValittu != null) && <>
        <label className="dim">{tr("tyo.kohde_iso")}
        <select value={valittu} onChange={(e) => setValittu(e.target.value ? Number(e.target.value) : "")}>
          <option value="">{tr("hub.valitse_kohde")}</option>
          {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
        </select></label></>}
      </div>

      {valittu !== "" && saaMuokata && <Uusi kohde_id={valittu} onLuotu={paivita} />}

      {lataa && <p className="dim">{tr("ltar.ladataan")}</p>}
      {!lataa && valittu !== "" && paketit.length === 0 && (
        <p className="dim" data-tyhja-tila="">{tr("tyo.ei_paketteja")}{saaMuokata ? tr("tyo.luo_ensimmainen") : ""}</p>
      )}

      <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
        {paketit.map((p) => (
          <div key={p.id} data-tyopaketti={p.id} data-korostettu={korostettu === p.id ? "" : undefined}
               style={korostettu === p.id ? { borderLeft: "4px solid var(--muste)", paddingLeft: 6 } : undefined}>
            <Kortti p={p} kohde_id={valittu as number} resurssit={resurssit}
              saaMuokata={saaMuokata} onMuutos={paivita} />
          </div>
        ))}
      </div>
    </>
  );
}

/* ── uuden paketin luonti ─────────────────────────────────── */
function Uusi({ kohde_id, onLuotu }: { kohde_id: number; onLuotu: () => void }) {
  const [auki, setAuki] = useState(false);
  const [nimi, setNimi] = useState("");
  const [media, setMedia] = useState("");
  const [dn, setDn] = useState("");
  const [haku, setHaku] = useState("");
  const [osumat, setOsumat] = useState<Linja[]>([]);
  const [valitut, setValitut] = useState<Linja[]>([]);
  /* T288 + T289 (13.9.2026): ehdotukset ja valitut ovat KAKSI listaa. Ennen «Ehdota linjat» lisäsi jokaisen
     ehdotetun suoraan valituiksi (karsinta × linja kerrallaan), ja uusi haku kertyi vanhan päälle — DN:n vaihdon
     jälkeen listassa oli molempien DN:ien linjat eikä mikään kertonut kumpi oli ehdotus ja kumpi päätös.
     Nyt ehdotus on valitsematon siru, napautus siirtää valituksi, ja «Hae ehdotukset uudelleen» korvaa vain
     ehdotukset: napautetut ovat käyttäjän päätöksiä ja säilyvät. */
  const [ehdotetut, setEhdotetut] = useState<Linja[]>([]);
  const [ehdotusPeruste, setEhdotusPeruste] = useState("");
  /** Montako linjaa haku löysi. Toni 13.9.: haku DN140 löysi 0, ja näkymä sanoi «Ei valitsemattomia ehdotuksia»
   *  — kuin kaikki löydetyt olisi jo valittu. Nolla löydettyä ja kaikki valittuna ovat eri tiloja. */
  const [ehdotusLoydetty, setEhdotusLoydetty] = useState(0);
  const [tallentaa, setTallentaa] = useState(false);
  const [ehdottaa, setEhdottaa] = useState(false);

  const ehdotaLinjat = async () => {
    if (!media.trim() && !dn.trim()) return;
    setEhdottaa(true);
    try {
      const l = await api.ehdotaLinjat(kohde_id, media.trim() || undefined, dn.trim() || undefined);
      const valitutIdt = new Set(valitut.map((x) => x.id));
      setEhdotetut(l.filter((x) => !valitutIdt.has(x.id)));
      setEhdotusLoydetty(l.length);
      setEhdotusPeruste([media.trim(), dn.trim() && `DN${dn.trim()}`].filter(Boolean).join(" "));
    } finally { setEhdottaa(false); }
  };

  const valitseEhdotus = (l: Linja) => {
    setValitut((v) => (v.some((x) => x.id === l.id) ? v : [...v, l]));
    setEhdotetut((e) => e.filter((x) => x.id !== l.id));
  };

  useEffect(() => {
    if (!auki || haku.trim().length < 1) { setOsumat([]); return; }
    let elossa = true;
    api.linjat(haku.trim(), kohde_id, 30).then((l) => { if (elossa) setOsumat(l); }).catch(() => {});
    return () => { elossa = false; };
  }, [haku, auki, kohde_id]);

  const lisaaLinja = (l: Linja) => {
    if (!valitut.some((x) => x.id === l.id)) setValitut([...valitut, l]);
    setEhdotetut((e) => e.filter((x) => x.id !== l.id));
    setHaku(""); setOsumat([]);
  };

  const tallenna = async () => {
    if (!nimi.trim()) return;
    setTallentaa(true);
    try {
      await api.luoTyopaketti({
        kohde_id, nimi: nimi.trim(),
        media: media.trim() || null, dn: dn.trim() || null,
        linja_ids: valitut.map((l) => l.id),
      });
      setNimi(""); setMedia(""); setDn(""); setValitut([]); setHaku(""); setEhdotetut([]); setEhdotusPeruste(""); setEhdotusLoydetty(0);
      setAuki(false); onLuotu();
    } finally { setTallentaa(false); }
  };

  if (!auki) {
    return <button onClick={() => setAuki(true)}>{tr("tyo.uusi")}</button>;
  }

  const kortti: CSSProperties = { background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 16 };
  return (
    <div style={kortti}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "2 1 220px" }}>
          <label className="dim" style={{ display: "block" }}>{tr("tyo.paketin_nimi")}</label>
          <input value={nimi} onChange={(e) => setNimi(e.target.value)} placeholder={tr("tyo.nimi_esim")} style={{ width: "100%" }} />
        </div>
        {/* Selaintarkistus 13.9.: kapeaan kenttään «esim. CPH» ei mahdu, ja esimerkki huusi kovempaa kuin kentän nimi.
            Kapeissa kentissä esimerkki on otsikossa ja kenttä on tyhjä; leveissä vihje pysyy kentässä. */}
        <div style={{ flex: "0 1 110px" }}>
          <label style={{ display: "block" }}>{tr("tyo.media_iso")} <span style={{ textTransform: "none", letterSpacing: 0 }}>{tr("tyo.media_esim")}</span></label>
          <input value={media} onChange={(e) => setMedia(e.target.value)} style={{ width: "100%" }} />
        </div>
        <div style={{ flex: "0 1 100px" }}>
          <label style={{ display: "block" }} title={tr("tyo.dn_title")}>DN <span style={{ textTransform: "none", letterSpacing: 0 }}>{tr("tyo.dn_esim")}</span></label>
          <input value={dn} onChange={(e) => setDn(e.target.value)} title={tr("tyo.dn_title")} style={{ width: "100%" }} />
        </div>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" onClick={ehdotaLinjat} disabled={(!media.trim() && !dn.trim()) || ehdottaa}
          title={tr("tyo.ehdota_title")}>
          {ehdottaa ? tr("tyo.haetaan")
            : (ehdotusPeruste ? tr("tyo.hae_uudelleen") : tr("tyo.ehdota"))
              + (media.trim() || dn.trim() ? ` (${[media.trim(), dn.trim() && `DN${dn.trim()}`].filter(Boolean).join(" ")})` : "")}
        </button>
        {valitut.length > 0 && (
          <span className="dim" >
            {tr("tyo.valitut_yhteenveto", { n: valitut.length, i: valitut.filter((l) => l.isometri_olemassa).length,
              h: valitut.reduce((s, l) => s + (l.suunniteltuja_hitseja ?? 0), 0) || "?" })}
          </span>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <label className="dim" style={{ display: "block" }}>{tr("tyo.linjat_haku")}</label>
        <input value={haku} onChange={(e) => setHaku(e.target.value)} placeholder={tr("tyo.haku_esim")} style={{ width: "100%", maxWidth: 320 }} />
        {osumat.length > 0 && (
          <div style={{ border: "1px solid var(--viiva)", marginTop: 4, maxWidth: 320, maxHeight: 160, overflowY: "auto" }}>
            {osumat.map((l) => (
              <div key={l.id} onClick={() => lisaaLinja(l)}
                style={{ padding: "5px 8px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid var(--viiva)" }}>
                {l.tunnus}
              </div>
            ))}
          </div>
        )}
        {ehdotusPeruste && (
          <div style={{ marginTop: 10 }} data-ehdotetut={ehdotetut.length}>
            <label className="dim" style={{ display: "block" }}>
              {tr("tyo.ehdotukset", { p: ehdotusPeruste })}
            </label>
            {ehdotetut.length === 0
              ? (ehdotusLoydetty === 0
                  ? <span className="dim">{tr("tyo.ei_loytynyt", { p: ehdotusPeruste })}
                      {/* Cowork 13.9.: teksti selitti käsitteet mutta ei kertonut mitä tehdä — nyt se sanoo sen tällä luvulla. */}
                      {dn.trim() && (
                        <> {tr("tyo.jos_alue", { dn: dn.trim() })}{" "}
                          <button type="button" onClick={() => setHaku(`${dn.trim()}-`)}
                                  style={{ background: "none", border: 0, padding: 0, font: "inherit", color: "inherit",
                                           textDecoration: "underline", cursor: "pointer" }}>
                            {tr("tyo.hae_alue", { dn: dn.trim() })}
                          </button>.</>
                      )}</span>
                  : <span className="dim">{tr("tyo.kaikki_valittu", { n: ehdotusLoydetty })}</span>)
              : (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                  {ehdotetut.map((l) => (
                    <button key={l.id} type="button" onClick={() => valitseEhdotus(l)}
                            style={{ ...chip, borderStyle: "dashed", background: "transparent", cursor: "pointer" }}
                            title={tr("tyo.napauta_title")}>
                      + {l.tunnus}
                    </button>
                  ))}
                </div>
              )}
          </div>
        )}
        {valitut.length > 0 && (
          <div style={{ marginTop: 12 }} data-valitut={valitut.length}>
          <label style={{ display: "block", fontSize: 11, letterSpacing: ".1em", fontWeight: 700,
                          color: "var(--korostus-600)" }}>
            {tr("tyo.valitut_pakettiin", { n: valitut.length })}
          </label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
            {valitut.map((l) => (
              <span key={l.id} style={{ ...chip, border: "2px solid var(--korostus-600)", fontWeight: 600 }}>
                ✓ {l.tunnus}
                <b onClick={() => setValitut(valitut.filter((x) => x.id !== l.id))} style={rasti}>×</b>
              </span>
            ))}
          </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
        <button onClick={tallenna} disabled={!nimi.trim() || tallentaa}>{tallentaa ? tr("pu.tallennetaan") : tr("tyo.luo_paketti")}</button>
        <button onClick={() => setAuki(false)} className="toissijainen">{tr("hl.peruuta")}</button>
      </div>
    </div>
  );
}

/* ── yksittäinen työpaketti ───────────────────────────────── */
function Kortti({ p, kohde_id, resurssit, saaMuokata, onMuutos }:
  { p: Tyopaketti; kohde_id: number; resurssit: Resurssi[]; saaMuokata: boolean; onMuutos: () => void }) {
  const [lisaaLinjaHaku, setLisaaLinjaHaku] = useState("");
  const [osumat, setOsumat] = useState<Linja[]>([]);
  const [tekija, setTekija] = useState<number | "">("");
  const [matAuki, setMatAuki] = useState(false);
  const [mat, setMat] = useState<TyopakettiMateriaalit | null>(null);
  const [isoAuki, setIsoAuki] = useState<number | null>(null);
  const [tarve, setTarve] = useState<TyopakettiTarve[] | null>(null);
  const [ottaa, setOttaa] = useState(false);

  const lataaTarve = () => api.tyopakettiTarve(p.id).then(setTarve).catch(() => setTarve([]));
  const avaaMateriaalit = () => {
    if (matAuki) { setMatAuki(false); return; }
    setMatAuki(true);
    if (!mat) api.tyopakettiMateriaalit(p.id).then(setMat).catch(() => setMat(null));
    if (!tarve) lataaTarve();
  };

  async function kirjaaOtot() {
    if (!tarve) return;
    const rivit = tarve
      .filter((t) => t.varasto_nimike_id && (t.tarve - t.jo_otettu) > 0.0001)
      .map((t) => ({ varasto_nimike_id: t.varasto_nimike_id as number, maara: t.tarve - t.jo_otettu }));
    if (rivit.length === 0) return;
    setOttaa(true);
    try { await api.kirjaaTyopakettiOtot(p.id, rivit); await lataaTarve(); onMuutos(); }
    finally { setOttaa(false); }
  }

  useEffect(() => {
    if (lisaaLinjaHaku.trim().length < 1) { setOsumat([]); return; }
    let elossa = true;
    api.linjat(lisaaLinjaHaku.trim(), kohde_id, 20).then((l) => { if (elossa) setOsumat(l); }).catch(() => {});
    return () => { elossa = false; };
  }, [lisaaLinjaHaku, kohde_id]);

  const osuus = p.hitseja > 0 ? Math.round((100 * p.tehty) / p.hitseja) : 0;
  const vapaat = resurssit.filter((r) => !(p.tekijat ?? []).some((t) => t.resurssi_id === r.id));

  const kortti: CSSProperties = { background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 16 };
  return (
    <div style={kortti}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }}>
            <span className="dim mono" style={{ fontSize: 13 }}>#{p.nro}</span> {p.nimi}
          </div>
          <div className="dim" style={{ marginTop: 2 }}>
            {[p.media, p.dn && `DN${p.dn}`, tr("tyo.hitsia", { n: `${p.tehty}/${p.hitseja}` })]
              .filter(Boolean).join(" · ")}
          </div>
        </div>
        {saaMuokata ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* 235 (T520, Toni 18.9.2026): tilan siirto taaksepäin vaatii perusteen — palvelin kieltäytyy ilman (409 tila_peruste_puuttuu). */}
            <label>{tr("tyo.tila_otsikko")}<select value={p.tila} data-tyopaketti-tila={p.id} onChange={(e) => {
              const uusi = e.target.value;
              const taakse = TILAT.indexOf(uusi) < TILAT.indexOf(p.tila);
              const peruste = taakse ? (window.prompt(tr("tyo.tila_peruste_kysymys", { vanha: tilanNimi(p.tila), uusi: tilanNimi(uusi) })) ?? "").trim() : "";
              if (taakse && !peruste) { e.target.value = p.tila; return; }
              void api.muokkaaTyopaketti(p.id, { tila: uusi, ...(peruste ? { tila_peruste: peruste } : {}) }).then(onMuutos)
                .catch((v) => alert(v instanceof Error ? v.message : String(v)));
            }}>
              {TILAT.map((ti) => <option key={ti} value={ti}>{tilanNimi(ti)}</option>)}
            </select></label>
            {/* 238 (Toni 13.10.2026): «Poista» poisti paketin kaskadina — nyt arkistointi perusteella, rivi jää. */}
            {p.arkistoitu && <span className="dim" data-tyopaketti-arkistoitu={p.id}>{tr("tyo.arkistoitu", { peruste: p.arkistoinnin_peruste ?? "" })}</span>}
            {p.arkistoitu
              ? <button className="toissijainen" data-tyopaketti-palauta={p.id}
                  onClick={() => void api.palautaTyopaketti(p.id).then(onMuutos)}>{tr("tyo.palauta")}</button>
              : <button className="toissijainen" title={tr("tyo.arkistoi_title")} data-tyopaketti-arkistoi={p.id}
                  onClick={() => {
                    const peruste = (window.prompt(tr("tyo.arkistoi_kysymys", { nro: p.nro, nimi: p.nimi })) ?? "").trim();
                    if (peruste) void api.arkistoiTyopaketti(p.id, peruste).then(onMuutos).catch((v) => alert(v instanceof Error ? v.message : String(v)));
                  }}>{tr("tyo.arkistoi")}</button>}
          </div>
        ) : <span className="dim" data-tila={p.tila}>{tilanNimi(p.tila)}</span>}
      </div>

      {/* 235 (T520): suunnitellut päivät — kolmen viikon suunnitelma. Toteutuneet (aloitettu, valmis) ovat eri kenttiä. */}
      <div className="tyopaketti__paivat" data-tyopaketti-paivat={p.id}>
        {saaMuokata ? (<>
          <label>{tr("tyo.suunniteltu_alku")}<input type="date" defaultValue={p.suunniteltu_alku ?? ""}
            onChange={(e) => void api.muokkaaTyopaketti(p.id, { suunniteltu_alku: e.target.value || null }).then(onMuutos).catch((v) => alert(v instanceof Error ? v.message : String(v)))} /></label>
          <label>{tr("tyo.suunniteltu_loppu")}<input type="date" defaultValue={p.suunniteltu_loppu ?? ""}
            onChange={(e) => void api.muokkaaTyopaketti(p.id, { suunniteltu_loppu: e.target.value || null }).then(onMuutos).catch((v) => alert(v instanceof Error ? v.message : String(v)))} /></label>
        </>) : (p.suunniteltu_alku || p.suunniteltu_loppu) ? (
          <span className="dim">{tr("tyo.suunniteltu")}: {naytaPvm(p.suunniteltu_alku) ?? "?"} – {naytaPvm(p.suunniteltu_loppu) ?? "?"}</span>
        ) : null}
      </div>

      {/* etenemä */}
      <div style={{ height: 7, background: "var(--sinkki)", marginTop: 12 }}>
        <div style={{ height: "100%", width: `${Math.min(osuus, 100)}%`, background: "var(--korostus)" }} />
      </div>
      <div className="dim" style={{ marginTop: 3 }}>{tr("tyo.osuus", { p: osuus })}</div>

      {/* linjat */}
      <div style={{ marginTop: 14 }}>
        <div className="dim" style={{ marginBottom: 4 }}>{tr("tyo.linjat_n", { n: p.linjat.length })}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {p.linjat.map((l) => (
            <span key={l.linja_id} style={chip}>
              {l.tunnus}
              {saaMuokata && <b onClick={() => api.poistaTyopakettiLinja(p.id, l.linja_id).then(onMuutos)} style={rasti}>×</b>}
            </span>
          ))}
          {p.linjat.length === 0 && <span className="dim" >{tr("tyo.ei_linjoja")}</span>}
        </div>
        {saaMuokata && <div style={{ marginTop: 8, position: "relative", maxWidth: 300 }}>
          <input value={lisaaLinjaHaku} onChange={(e) => setLisaaLinjaHaku(e.target.value)}
            placeholder={tr("tyo.lisaa_linja")} style={{ width: "100%" }} />
          {osumat.length > 0 && (
            <div style={{ border: "1px solid var(--viiva)", marginTop: 2, maxHeight: 140, overflowY: "auto", background: "var(--pelti)" }}>
              {osumat.filter((l) => !p.linjat.some((x) => x.linja_id === l.id)).map((l) => (
                <div key={l.id} onClick={() => { api.lisaaTyopakettiLinja(p.id, l.id).then(onMuutos); setLisaaLinjaHaku(""); setOsumat([]); }}
                  style={{ padding: "5px 8px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid var(--viiva)" }}>
                  {l.tunnus}
                </div>
              ))}
            </div>
          )}
        </div>}
      </div>

      {/* tekijät — lohko puuttuu kokonaan kun kenttä puuttuu (tilaaja, 26.9.2026): «ei tekijöitä»
          olisi väite eikä rajaus, sama sopimus kuin Hubin Miehitys-kortilla. */}
      {p.tekijat && <div style={{ marginTop: 14 }}>
        <div className="dim" style={{ marginBottom: 4 }}>{tr("tyo.tekijat_n", { n: p.tekijat.length })}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {p.tekijat.map((t) => (
            <span key={t.resurssi_id} style={chip}>
              {t.lyhenne ? t.lyhenne + " · " : ""}{t.nimi}
              {saaMuokata && <b onClick={() => api.poistaTyopakettiTekija(p.id, t.resurssi_id).then(onMuutos)} style={rasti}>×</b>}
            </span>
          ))}
          {p.tekijat.length === 0 && <span className="dim" >{tr("tyo.ei_tekijoita")}</span>}
          {saaMuokata && <label>{tr("tyo.lisaa_tekija_otsikko")}<select value={tekija} onChange={(e) => {
            const id = Number(e.target.value);
            if (id) { api.lisaaTyopakettiTekija(p.id, id).then(onMuutos); setTekija(""); }
          }}>
            <option value="">{tr("tyo.lisaa_tekija")}</option>
            {vapaat.map((r) => (
              <option key={r.id} value={r.id}>{r.lyhenne ? r.lyhenne + " · " : ""}{r.nimi}</option>
            ))}
          </select></label>}
        </div>
      </div>}

      {/* isometrit & materiaalit */}
      <div style={{ marginTop: 14, borderTop: "1px solid var(--viiva)", paddingTop: 12 }}>
        <button className="toissijainen" onClick={avaaMateriaalit}>
          {matAuki ? tr("tyo.piilota") : tr("tyo.isometrit_materiaalit")}
        </button>
        {matAuki && (
          <div style={{ marginTop: 12 }}>
            {!mat && <div className="dim" >{tr("ltar.ladataan")}</div>}
            {mat && (
              <>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  {/* isometrit */}
                  <div style={{ flex: "1 1 260px", minWidth: 240, maxWidth: "100%" }}>
                    <div className="dim" style={{ marginBottom: 4 }}>
                      {tr("tyo.isometrit_n", { n: mat.isometrit.length })}
                    </div>
                    {mat.isometrit.map((i) => (
                      <div key={i.linja_id}>
                        <div onClick={() => setIsoAuki(isoAuki === i.linja_id ? null : i.linja_id)}
                          style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5, padding: "3px 0", cursor: "pointer" }}>
                          <span style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>📐 {i.tunnus}</span>
                          <span className="mono dim" style={{ whiteSpace: "nowrap" }}>
                            {i.isometri_olemassa ? "✓" : "—"} · {i.hitseja} {tr("tyo.h")}
                          </span>
                        </div>
                        {isoAuki === i.linja_id && (
                          <div style={{ margin: "2px 0 8px", paddingLeft: 8, borderLeft: "2px solid var(--korostus)" }}>
                            <Liitteet kohdeTaulu="linja" kohdeId={i.linja_id}
                              saaMuokata={saaMuokata} otsikko={tr("tyo.isometri")} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* materiaalit */}
                  <div style={{ flex: "2 1 340px", minWidth: 280, maxWidth: "100%", overflow: "hidden" }}>
                    <div className="dim" style={{ marginBottom: 4 }}>
                      {tr("tyo.materiaalit_n", { n: mat.materiaalit.length })}
                    </div>
                    {mat.materiaalit.length === 0 && <div className="dim" >{tr("tyo.ei_osia")}</div>}
                    {mat.materiaalit.map((m, idx) => {
                      const sul = m.sulatukset ? m.sulatukset.split(", ") : [];
                      return (
                        <div key={idx} style={{ padding: "3px 0", borderBottom: "1px solid var(--viiva)", fontSize: 12.5 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                            <span>{m.nimike}{m.materiaali ? ` · ${m.materiaali}` : ""}</span>
                            <span className="mono dim" style={{ whiteSpace: "nowrap" }}>
                              {tr("tyo.kpl", { n: m.kpl })}{sul.length ? ` · ${tr("tyo.sul", { n: sul.length })}` : ""}
                            </span>
                          </div>
                          {sul.length > 0 && (
                            <div className="mono dim" style={{ fontSize: 10, marginTop: 2, opacity: 0.7,
                              overflowWrap: "anywhere", wordBreak: "break-word" }}>
                              {m.sulatukset}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {tarve && tarve.length > 0 && (
                  <div style={{ marginTop: 16, borderTop: "1px solid var(--viiva)", paddingTop: 12 }}>
                    <div className="dim" style={{ marginBottom: 6 }}>
                      {tr("tyo.tarve_otsikko", { n: tarve.length })}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table className="tbl" style={{ fontSize: 12, minWidth: 460 }}>
                        <thead><tr>
                          <th>{tr("tyo.nimike")}</th>
                          <th style={{ textAlign: "right" }}>{tr("tyo.tarve")}</th>
                          <th style={{ textAlign: "right" }}>{tr("tyo.saldo")}</th>
                          <th style={{ textAlign: "right" }}>{tr("tyo.otettu")}</th>
                          <th style={{ textAlign: "right" }}>{tr("tyo.viela")}</th>
                        </tr></thead>
                        <tbody>
                          {tarve.map((t) => {
                            const viela = t.tarve - t.jo_otettu;
                            const vaje = t.varasto_nimike_id != null && t.saldo < viela;
                            const f = (n: number) => n.toLocaleString("fi-FI", { maximumFractionDigits: 2 });
                            return (
                              <tr key={t.nimike_tunnus}>
                                <td>{t.nimike_tunnus}{t.nimi ? ` · ${t.nimi}` : ""}
                                  {t.varasto_nimike_id == null && <span className="dim"> {tr("tyo.ei_varastossa")}</span>}</td>
                                <td className="mono" style={{ textAlign: "right" }}>{f(t.tarve)} {t.yksikko}</td>
                                <td className="mono" style={{ textAlign: "right", color: vaje ? "var(--korostus)" : undefined }}>
                                  {t.varasto_nimike_id != null ? f(t.saldo) : "—"}</td>
                                <td className="mono" style={{ textAlign: "right" }}>{t.jo_otettu ? f(t.jo_otettu) : ""}</td>
                                <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>
                                  {viela > 0.0001 ? f(viela) : "✓"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {saaMuokata && tarve.some((t) => t.varasto_nimike_id && (t.tarve - t.jo_otettu) > 0.0001) && (
                      <button className="nappi nappi--paa" style={{ marginTop: 8 }} disabled={ottaa} onClick={kirjaaOtot}>
                        {ottaa ? tr("hl.kirjataan") : tr("tyo.kirjaa_otot")}
                      </button>
                    )}
                  </div>
                )}
                {tarve && tarve.length === 0 && (
                  <div className="dim" style={{ marginTop: 12 }}>
                    {tr("tyo.ei_tarvetta")}
                  </div>
                )}

                <button className="toissijainen" style={{ marginTop: 12 }}
                  onClick={() => tulostaPaketti(p, mat)}>{tr("tyo.tulosta")}</button>
                {/* Lupauslause poistettu 18.9.2026 (Toni): «Tilausta varten tarvittava esitäyttö
                    (MTO) tulee Varasto-vaiheessa; isometritiedostojen linkit isometrien tuonnin
                    myötä.» MTO-esitäyttö on jo tässä näkymässä, eikä tilaustukea ole — tiekartalla.
                    Sääntö: tuotteen pinnalla saa lukea vain se, mikä on ajossa. */}
                <p className="dim" style={{ marginTop: 8 }}>
                  {tr("tyo.as_built")}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* Tulostettava lista: aukeaa omaan ikkunaan ja avaa tulostusdialogin. */
function tulostaPaketti(p: Tyopaketti, mat: TyopakettiMateriaalit) {
  const esc = (s: string) => s.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]!));
  const iso = mat.isometrit.map((i) =>
    `<tr><td>${esc(i.tunnus)}</td><td>${i.isometri_olemassa ? "✓" : "—"}</td><td style="text-align:right">${i.hitseja}</td></tr>`).join("");
  const materi = mat.materiaalit.map((m) =>
    `<tr><td>${esc(m.nimike)}</td><td>${esc(m.materiaali ?? "")}</td><td style="text-align:right">${m.kpl}</td><td>${esc(m.sulatukset ?? "")}</td></tr>`).join("");
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Työpaketti #${p.nro} — ${esc(p.nimi)}</title>
    <style>body{font:13px/1.5 Arial,sans-serif;margin:28px;color:#111}
    h1{font-size:20px;margin:0 0 2px}h2{font-size:13px;text-transform:uppercase;letter-spacing:.08em;color:#555;margin:22px 0 6px}
    .meta{color:#555;font-size:12px;margin-bottom:6px}
    table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:5px 8px;font-size:12px;text-align:left}
    th{background:#f0f0f0}</style></head><body>
    <h1>Työpaketti #${p.nro} — ${esc(p.nimi)}</h1>
    <div class="meta">${[p.media, p.dn && "DN" + p.dn, p.tila, p.tehty + "/" + p.hitseja + " hitsiä"].filter(Boolean).join(" · ")}</div>
    <h2>Isometrit (${mat.isometrit.length})</h2>
    <table><thead><tr><th>Linja / isometri</th><th>Isometri</th><th>Hitsejä</th></tr></thead><tbody>${iso}</tbody></table>
    <h2>Materiaalit — hitsatut osat (${mat.materiaalit.length})</h2>
    <table><thead><tr><th>Nimike</th><th>Materiaali</th><th>kpl</th><th>Sulatukset</th></tr></thead><tbody>${materi}</tbody></table>
    </body></html>`);
  w.document.close(); w.focus();
  setTimeout(() => w.print(), 300);
}

const chip: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
  background: "var(--sinkki)", border: "1px solid var(--viiva)",
  padding: "3px 8px", fontSize: 12.5,
};
const rasti: CSSProperties = { cursor: "pointer", color: "var(--himmea, #888)", fontWeight: 700 };
